import styles from './InlineGenerationPrompt.module.scss'
import { useRef, useState } from 'react'
import { VoicesAndTonesSelect } from 'components/DocRightRail/screens/AIAssistant/VoicesAndTonesSelect'
import { Tone } from 'components/DocRightRail/screens/AIAssistant/ToneSelect'
import BaseStyles from 'components/BlazeCommandLine/BlazeCommandLine.module.scss'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { ArticleFlat } from 'types'
import { EditorView } from 'prosemirror-view'
import { NodeSelection } from 'prosemirror-state'
import { PromptInput } from 'components/PromptInput/PromptInput'
import { useInteractOutside } from '@react-aria/interactions'
import { markdownSerializer } from '../TextInlineFormatBar/components/FormatBars/markdownSerializer'
import { injectContent } from 'common/Prose/utils/injectContent'
import { useCreateAISuggestionNode } from 'hooks/queries/layers/aiSuggestionNodes/useCreateAISuggestionNode'

interface Props {
  hideAIDocBar: () => void
  article: ArticleFlat
  editorView: EditorView
  selection: NodeSelection
}

export const InlineGenerationPrompt = ({ hideAIDocBar, article, editorView, selection }: Props) => {
  const [prompt, setPrompt] = useState('')
  const [brandVoiceId, setBrandVoiceId] = useState<number>()
  const [tones, setTones] = useState<Array<Tone>>([])

  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const mutation = useCreateAISuggestionNode({ articleId: article.id, layerId: article.main_layer.id })
  useInteractOutside({ ref: wrapperRef, onInteractOutside: hideAIDocBar })

  const handleClose = () => {
    hideAIDocBar()
    editorView.focus()
  }

  const onGenerate = () => {
    const newDoc = injectContent(editorView.state, selection, '<<AI_CONTENT>>')
    const documentAsMarkdown = markdownSerializer.serialize(newDoc)

    mutation.mutate({
      selection,
      documentAsMarkdown: documentAsMarkdown,
      prompt: {
        generation_type: GenerationType.InlineGeneric,
        brand_voice_id: brandVoiceId,
        tone: tones,
        topic: prompt,
      },
    })
    hideAIDocBar()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation()
    if (e.key === 'Escape') {
      handleClose()
    } else if (e.key === 'Enter' && !e.shiftKey && prompt.trim().length > 0) {
      e.preventDefault()
      onGenerate()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // Check if the input is focused
    if (document.activeElement === inputRef.current) {
      e.stopPropagation() // This prevents the event from reaching the ProseMirror document
    }
  }

  return (
    <div ref={wrapperRef} className={styles.container}>
      <div className={styles.promptContainer}>
        <div className={styles.promptInputContainer}>
          <PromptInput
            prompt={prompt}
            onChange={setPrompt}
            placeholder="Ask Blaze to generate..."
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onSubmit={onGenerate}
            ref={(input) => {
              input?.focus()
              inputRef.current = input
            }}
          />
        </div>
      </div>
      <div className={styles.brandVoiceContainer}>
        <span className={styles.voiceLabel}>Select your brand voice</span>
        <VoicesAndTonesSelect
          className={BaseStyles.voicesAndTones}
          onVoicesChange={setBrandVoiceId}
          onTonesChange={setTones}
          selectedVoice={brandVoiceId}
          selectedTones={tones}
          isVoicesInline
          isTonesInline
        />
      </div>
    </div>
  )
}
